import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs';
import { SupportAccessService } from './support-access.service';

export type SupportTab = 'new' | 'mine' | 'manage';

const DEEP_LINK_PARAM = 'support';
const DEEP_LINK_STORAGE_KEY = 'support.deepLink';
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface DeepLinkInUrl {
  /** Waarde van `support=` vóór de hash (`?support=...#/pad`); undefined als afwezig. */
  beforeHash?: string;
  /** Waarde van `support=` in de hash-query (`#/pad?support=...`); undefined als afwezig. */
  inHash?: string;
}

/** Open/dicht-toestand, actieve tab en geselecteerde melding van het support-paneel. */
@Injectable({ providedIn: 'root' })
export class SupportPanelService {
  private readonly router = inject(Router);
  private readonly access = inject(SupportAccessService);

  readonly open = signal(false);
  readonly activeTab = signal<SupportTab>('new');
  readonly selectedFeedbackId = signal<string | undefined>(undefined);
  /**
   * Eenmalige overdracht van een deep link aan de beheer-tab: de beheerlijst
   * neemt dit id bij haar start over en wist het weer, zodat de selectie
   * daarna gewoon eigen toestand van de lijst blijft.
   */
  readonly manageFeedbackId = signal<string | undefined>(undefined);
  /** Verbergt de handle tijdelijk (screenshot-flow). */
  readonly handleHidden = signal(false);

  constructor() {
    // Uitloggen: de stash hoort niet bij een volgende gebruiker in dezelfde tab.
    // Alleen op de overgang true -> false: bij de start is canUse nog false en
    // moet de zonet gestashte deep link een login-redirect juist overleven.
    let couldUse = false;
    effect(() => {
      const canUse = this.access.canUse();
      if (couldUse && !canUse) untracked(() => this.clearStash());
      couldUse = canUse;
    });
  }

  openAt(tab: SupportTab, feedbackId?: string): void {
    this.activeTab.set(tab);
    this.selectedFeedbackId.set(feedbackId);
    this.open.set(true);
  }

  close(): void {
    this.open.set(false);
  }

  toggle(): void {
    this.open.update(value => !value);
  }

  /**
   * Bewaart een geldig `support=<guid>` uit de URL in sessionStorage, zodat de
   * deep link een login-redirect overleeft. Aan te roepen vóór de eerste routing.
   */
  stashDeepLink(): void {
    const id = this.validId(this.readDeepLinkFromUrl());
    if (!id) return;
    try {
      sessionStorage.setItem(DEEP_LINK_STORAGE_KEY, id);
    } catch {
      // Storage niet beschikbaar (privémodus, quota): de URL-variant volstaat dan.
    }
  }

  /**
   * Leest `support=<guid>` uit de URL (vóór en in de hash) of uit de stash,
   * opent de melding (beheerders in de beheer-tab, melders onder "Mijn
   * meldingen") en wist beide bronnen. Een ongeldig id wordt alleen opgeruimd.
   */
  consumeDeepLink(): void {
    const inUrl = this.readDeepLinkFromUrl();
    const stashed = this.takeStashedId();
    const id = this.validId(inUrl) ?? stashed;
    this.removeFromUrl(inUrl);
    if (!id) return;

    // Een beheerder krijgt de notificatiemail juist over andermans melding;
    // eigen-scope zou daarop stranden (400). Beheerders landen dus in de
    // beheer-tab, die alle meldingen ziet; melders onder "Mijn meldingen".
    if (this.access.canManage()) {
      this.manageFeedbackId.set(id);
      this.openAt('manage');
      return;
    }
    this.openAt('mine', id);
  }

  private readDeepLinkFromUrl(): DeepLinkInUrl {
    const url = new URL(location.href);
    const hashQueryIndex = url.hash.indexOf('?');
    const hashParams = new URLSearchParams(hashQueryIndex >= 0 ? url.hash.slice(hashQueryIndex + 1) : '');
    return {
      beforeHash: url.searchParams.get(DEEP_LINK_PARAM) ?? undefined,
      inHash: hashParams.get(DEEP_LINK_PARAM) ?? undefined,
    };
  }

  private validId(inUrl: DeepLinkInUrl): string | undefined {
    return [inUrl.beforeHash, inUrl.inHash].find(candidate => candidate !== undefined && GUID_PATTERN.test(candidate));
  }

  private takeStashedId(): string | undefined {
    try {
      const stashed = sessionStorage.getItem(DEEP_LINK_STORAGE_KEY) ?? undefined;
      sessionStorage.removeItem(DEEP_LINK_STORAGE_KEY);
      return stashed && GUID_PATTERN.test(stashed) ? stashed : undefined;
    } catch {
      return undefined;
    }
  }

  private clearStash(): void {
    try {
      sessionStorage.removeItem(DEEP_LINK_STORAGE_KEY);
    } catch {
      // Storage niet beschikbaar: dan is er ook niets gestasht.
    }
  }

  private removeFromUrl(inUrl: DeepLinkInUrl): void {
    if (inUrl.beforeHash !== undefined) {
      // Buiten de hash kent de Router de parameter niet; de adresbalk zelf bijwerken.
      const url = new URL(location.href);
      url.searchParams.delete(DEEP_LINK_PARAM);
      history.replaceState(history.state, '', url.toString());
    }
    if (inUrl.inHash !== undefined) {
      // In de hash beheert de Router de query; via de Router blijft zijn state gelijk
      // aan de adresbalk (anders zet queryParamsHandling 'merge' elders de parameter terug).
      this.whenRouterReady(() => void this.router.navigate([], {
        queryParams: { [DEEP_LINK_PARAM]: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      }));
    }
  }

  private whenRouterReady(action: () => void): void {
    if (this.router.navigated) {
      action();
      return;
    }
    this.router.events.pipe(filter(event => event instanceof NavigationEnd), take(1)).subscribe(action);
  }
}
